import { useState, useEffect } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import {
  Mail,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  FlaskConical,
  Save,
  FileText,
  Plus,
  Edit3,
  Trash2,
  Lock,
  Server,
  Inbox,
  X,
  Settings,
  RefreshCw,
  ShieldCheck,
  Bell,
} from 'lucide-react';
import { emailApi, storeApi, inboxEmailApi, type EmailRoutingSummary, type EmailTemplate, type StoreEmailConfig, type Store } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { Select } from '../components/ui/Select';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { DeliverabilitySection } from '../components/email/DeliverabilitySection';

// =============================================================================
// MAIN PAGE — Store cards grid (WooCommerce pattern)
// =============================================================================

export function EmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data
  const [stores, setStores] = useState<Store[]>([]);
  const [storeEmailConfigs, setStoreEmailConfigs] = useState<StoreEmailConfig[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [inboxConfigs, setInboxConfigs] = useState<any[]>([]);

  // Modal
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, storesRes, storeConfigsRes, inboxRes] = await Promise.all([
        emailApi.getTemplates(),
        storeApi.getAll(),
        emailApi.getStoreConfigs(),
        inboxEmailApi.getConfigs().catch(() => ({ data: { data: [] } })),
      ]);

      setTemplates(templatesRes.data.templates);
      setStores(storesRes.data);
      setStoreEmailConfigs(storeConfigsRes.data.configs);
      setInboxConfigs(inboxRes.data.data || []);
    } catch (err) {
      console.error('[EmailSettingsPage] Load error:', err);
      setError('Nie udało się pobrać ustawień');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Integracja Email</h1>
          <p className="text-text-secondary mt-2">
            Zarządzaj konfiguracją email dla Twoich sklepów — SMTP, skrzynki odbiorcze i szablony w jednym miejscu.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
          <XCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {stores.length === 0 ? (
        <div className="card animate-slide-up">
          <EmptyState
            icon={Mail}
            title="Brak sklepów"
            description="Aby skonfigurować email, najpierw dodaj sklep w sekcji Integracje → WooCommerce"
            buttonLabel="Przejdź do integracji"
            buttonTo="/integrations"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stores.map((store, index) => {
            const smtpCount = storeEmailConfigs.filter(c => c.storeId === store.id).length;
            const imapCount = inboxConfigs.filter((c: any) => c.storeId === store.id).length;
            const templateCount = templates.filter(t => t.storeId === store.id).length;
            const hasActiveSmtp = storeEmailConfigs.some(c => c.storeId === store.id && c.isActive);
            const hasActiveImap = inboxConfigs.some((c: any) => c.storeId === store.id && c.isActive);

            return (
              <div
                key={store.id}
                className="card animate-slide-up group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${
                      hasActiveSmtp || hasActiveImap
                        ? 'bg-gradient-to-br from-accent-primary to-accent-secondary'
                        : 'bg-surface-hover'
                    }`}>
                      <Mail className={`w-6 h-6 ${hasActiveSmtp || hasActiveImap ? 'text-dark-900' : 'text-text-muted'}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">{store.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {hasActiveSmtp && (
                          <span className="badge badge-success">SMTP</span>
                        )}
                        {hasActiveImap && (
                          <span className="badge badge-info">IMAP</span>
                        )}
                        {!hasActiveSmtp && !hasActiveImap && (
                          <span className="badge badge-error">Nieskonfigurowany</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedStore(store)}
                    className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all"
                    title="Konfiguruj email"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                    <span className="text-sm text-text-secondary flex items-center gap-2">
                      <Server className="w-4 h-4" /> Konta SMTP
                    </span>
                    <span className="text-sm text-accent-primary font-semibold">{smtpCount}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                    <span className="text-sm text-text-secondary flex items-center gap-2">
                      <Inbox className="w-4 h-4" /> Skrzynki odbiorcze
                    </span>
                    <span className="text-sm text-accent-primary font-semibold">{imapCount}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                    <span className="text-sm text-text-secondary flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Szablony
                    </span>
                    <span className="text-sm text-accent-primary font-semibold">{templateCount}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border-subtle">
                  <button
                    onClick={() => setSelectedStore(store)}
                    className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                  >
                    <Settings className="w-4 h-4" />
                    Konfiguruj
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-store Modal */}
      {selectedStore && (
        <EmailStoreModal
          store={selectedStore}
          emailConfigs={storeEmailConfigs.filter(c => c.storeId === selectedStore.id)}
          inboxConfigs={inboxConfigs.filter((c: any) => c.storeId === selectedStore.id)}
          onClose={() => setSelectedStore(null)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}

// Wyciąga unikalne domeny z aktywnych skrzynek SMTP + IMAP DLA TEGO sklepu.
// Modal "Konfiguracja Email" pokazuje wszystko per-sklep, więc dostarczalność
// też ograniczona do domen używanych w tym konkretnym sklepie.
function extractUniqueDomains(
  smtpConfigs: StoreEmailConfig[],
  inboxConfigs: Array<{ fromEmail?: string | null; isActive?: boolean }>,
): string[] {
  const set = new Set<string>();
  const addFromEmail = (email: string | null | undefined) => {
    if (!email) return;
    const at = email.lastIndexOf('@');
    if (at < 0) return;
    const domain = email.slice(at + 1).toLowerCase().trim();
    if (domain) set.add(domain);
  };
  smtpConfigs.filter(c => c.isActive).forEach(c => addFromEmail(c.fromEmail));
  inboxConfigs.filter(c => c.isActive).forEach(c => addFromEmail(c.fromEmail));
  return Array.from(set).sort();
}

// =============================================================================
// EMAIL STORE MODAL — Per-store config with horizontal tabs (like WooCommerce)
// =============================================================================

function EmailStoreModal({
  store,
  emailConfigs,
  inboxConfigs,
  onClose,
  onRefresh,
}: {
  store: Store;
  emailConfigs: StoreEmailConfig[];
  inboxConfigs: any[];
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [activeTab, setActiveTab] = useState<'inbox' | 'notifications'>('inbox');

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const tabs = [
    { id: 'inbox' as const, name: 'Skrzynka odbiorcza', icon: Inbox },
    { id: 'notifications' as const, name: 'Notyfikacje', icon: Bell },
  ];

  const domains = extractUniqueDomains(emailConfigs, inboxConfigs);

  return (
    <div
      className="fixed inset-0 bg-surface-base/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-surface-raised border border-border rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Konfiguracja Email</h2>
            <p className="text-sm text-text-secondary">{store.name} — {store.url}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-surface-hover/50 rounded-lg mb-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    activeTab === tab.id
                      ? 'bg-accent-primary text-dark-900'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.name}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'inbox' && (
            <div className="space-y-8">
              <section>
                <h3 className="text-base font-semibold text-text-primary mb-1">Odbieranie wiadomości</h3>
                <p className="text-sm text-text-secondary mb-4">Połączenia IMAP widoczne w module Wiadomości.</p>
                <ImapTabContent store={store} inboxConfigs={inboxConfigs} emailConfigs={emailConfigs} onRefresh={onRefresh} />
              </section>
              <section className="pt-6 border-t border-border-subtle">
                <h3 className="text-base font-semibold text-text-primary mb-1">Odpowiedzi operatora</h3>
                <p className="text-sm text-text-secondary mb-4">Połączenia SMTP używane do odpowiedzi ze skrzynki.</p>
                <SmtpTabContent store={store} emailConfigs={emailConfigs} onRefresh={onRefresh} />
              </section>
              <details className="pt-6 border-t border-border-subtle group">
                <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-text-primary">
                  <ShieldCheck className="w-4 h-4 text-accent-primary" />
                  Dostarczalność i szczegóły techniczne
                </summary>
                <div className="mt-4"><DeliverabilitySection domains={domains} /></div>
              </details>
            </div>
          )}

          {activeTab === 'notifications' && (
            <NotificationsTabContent store={store} emailConfigs={emailConfigs} inboxConfigs={inboxConfigs} />
          )}

        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border-subtle">
          <button onClick={onClose} className="btn-secondary flex-1">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// NOTIFICATIONS TAB — explicit operator/system routing
// =============================================================================

function NotificationsTabContent({
  store,
  emailConfigs,
  inboxConfigs,
}: {
  store: Store;
  emailConfigs: StoreEmailConfig[];
  inboxConfigs: Array<{ id: string; isActive?: boolean }>;
}) {
  const { showSuccess, showError } = useNotification();
  const [routing, setRouting] = useState<EmailRoutingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<'operator' | 'notification' | null>(null);
  const [testStates, setTestStates] = useState<Partial<Record<'operator' | 'notification', RoutingState>>>({});
  const [operatorReplyConfigId, setOperatorReplyConfigId] = useState('');
  const [notificationMode, setNotificationMode] = useState<'INBOX' | 'CUSTOM'>('INBOX');
  const [notificationEmailConfigId, setNotificationEmailConfigId] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    emailApi.getRouting(store.id)
      .then(({ data }) => {
        if (cancelled) return;
        setRouting(data.data);
        setOperatorReplyConfigId(data.data.operatorReplyConfigId || '');
        setNotificationMode(data.data.notificationMode);
        setNotificationEmailConfigId(data.data.notificationEmailConfigId || '');
      })
      .catch((error) => { if (!cancelled) showError(error, 'email'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [store.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await emailApi.updateRouting(store.id, {
        operatorReplyConfigId: operatorReplyConfigId || null,
        notificationMode,
        notificationEmailConfigId: notificationMode === 'CUSTOM'
          ? notificationEmailConfigId || null
          : null,
      });
      setRouting(data.data);
      setTestStates({});
      showSuccess('save', { message: 'Routing poczty został zapisany.' });
    } catch (error) {
      showError(error, 'email');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (channel: 'operator' | 'notification') => {
    setTesting(channel);
    try {
      const { data } = await emailApi.testRouting(store.id, channel);
      setTestStates(current => ({ ...current, [channel]: 'READY' }));
      showSuccess('save', { message: data.message || 'Testowy email został wysłany.' });
    } catch (error) {
      setTestStates(current => ({ ...current, [channel]: 'ERROR' }));
      showError(error, 'email');
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-accent-primary" /></div>;
  }

  const activeConfigs = emailConfigs.filter(config => config.isActive);
  const hasInbox = inboxConfigs.some(config => config.isActive);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RoutingStatusCard
          title="Odpowiedzi operatora"
          state={testStates.operator || (routing?.operator.ready && hasInbox ? 'READY' : 'CONFIG')}
          details={routing?.operator.ready
            ? `${routing.operator.fromName} <${routing.operator.fromEmail}> przez ${routing.operator.provider}`
            : 'Połącz aktywną skrzynkę IMAP z kontem SMTP.'}
        />
        <RoutingStatusCard
          title="Notyfikacje systemowe"
          state={testStates.notification || (routing?.notification.ready ? 'READY' : 'CONFIG')}
          details={routing?.notification.ready
            ? `${routing.notification.fromName} <${routing.notification.fromEmail}> przez ${routing.notification.provider}`
            : 'Wybierz połączenie SMTP dla notyfikacji.'}
        />
      </div>

      <section className="space-y-3 p-4 rounded-xl border border-border-subtle bg-surface-hover/20">
        <div>
          <h3 className="font-medium text-text-primary">Kanał odpowiedzi operatora</h3>
          <p className="text-xs text-text-secondary mt-1">Używany przez odpowiedzi ręczne i zatwierdzone szkice AI.</p>
        </div>
        <Select value={operatorReplyConfigId} onChange={event => setOperatorReplyConfigId(event.target.value)}>
          <option value="">Domyślne aktywne konto SMTP</option>
          {activeConfigs.map(config => (
            <option key={config.id} value={config.id}>{config.name} - {config.fromEmail}</option>
          ))}
        </Select>
      </section>

      <section className="space-y-4 p-4 rounded-xl border border-border-subtle bg-surface-hover/20">
        <div>
          <h3 className="font-medium text-text-primary">Kanał notyfikacji</h3>
          <p className="text-xs text-text-secondary mt-1">Faktury, statusy zamówień i automatyczne wiadomości systemowe.</p>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="radio" checked={notificationMode === 'INBOX'} onChange={() => setNotificationMode('INBOX')} className="mt-1" />
          <span>
            <span className="block text-sm font-medium text-text-primary">Używaj połączenia skrzynki odbiorczej</span>
            <span className="block text-xs text-text-secondary">Domyślnie ten sam adres i dostawca co odpowiedzi operatora.</span>
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="radio" checked={notificationMode === 'CUSTOM'} onChange={() => setNotificationMode('CUSTOM')} className="mt-1" />
          <span>
            <span className="block text-sm font-medium text-text-primary">Oddzielny dostawca SMTP</span>
            <span className="block text-xs text-text-secondary">Opcja zaawansowana, np. konto transakcyjne Resend.</span>
          </span>
        </label>
        {notificationMode === 'CUSTOM' && (
          <Select value={notificationEmailConfigId} onChange={event => setNotificationEmailConfigId(event.target.value)}>
            <option value="">Wybierz konto SMTP</option>
            {activeConfigs.map(config => (
              <option key={config.id} value={config.id}>{config.name} - {config.fromEmail}</option>
            ))}
          </Select>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Zapisz routing
        </button>
        <button onClick={() => handleTest('operator')} disabled={testing !== null} className="btn-secondary flex items-center gap-2">
          {testing === 'operator' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Test odpowiedzi
        </button>
        <button onClick={() => handleTest('notification')} disabled={testing !== null} className="btn-secondary flex items-center gap-2">
          {testing === 'notification' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Test notyfikacji
        </button>
      </div>
    </div>
  );
}

type RoutingState = 'READY' | 'CONFIG' | 'ERROR' | 'UNKNOWN';

function RoutingStatusCard({ title, state, details }: { title: string; state: RoutingState; details: string }) {
  const status = {
    READY: { label: 'Gotowe', textClass: 'text-success', icon: CheckCircle },
    CONFIG: { label: 'Wymaga konfiguracji', textClass: 'text-warning', icon: XCircle },
    ERROR: { label: 'Błąd połączenia', textClass: 'text-danger', icon: XCircle },
    UNKNOWN: { label: 'Nie można potwierdzić', textClass: 'text-text-muted', icon: XCircle },
  }[state];
  const StatusIcon = status.icon;
  return (
    <div className={`p-4 rounded-xl border ${
      state === 'READY' ? 'bg-success/5 border-success/20'
        : state === 'ERROR' ? 'bg-danger/5 border-danger/20'
          : 'bg-warning/5 border-warning/20'
    }`}>
      <div className="flex items-center gap-2">
        <StatusIcon className={`w-4 h-4 ${status.textClass}`} />
        <span className="text-sm font-medium text-text-primary">{title}</span>
        <span className={`ml-auto text-xs font-medium ${status.textClass}`}>
          {status.label}
        </span>
      </div>
      <p className="text-xs text-text-secondary mt-2 break-words">{details}</p>
    </div>
  );
}

// =============================================================================
// SMTP TAB — SMTP config list + inline add/edit form
// =============================================================================

function SmtpTabContent({
  store,
  emailConfigs,
  onRefresh,
}: {
  store: Store;
  emailConfigs: StoreEmailConfig[];
  onRefresh: () => void;
}) {
  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [editingConfig, setEditingConfig] = useState<StoreEmailConfig | null>(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [testingConfigId, setTestingConfigId] = useState<string | null>(null);

  const handleDeleteConfig = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń konfigurację email',
      message: 'Czy na pewno chcesz usunąć tę konfigurację email?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await emailApi.deleteStoreConfig(id);
      onRefresh();
    } catch (err) {
      showError(err, 'email');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await emailApi.setStoreConfigDefault(id);
      onRefresh();
    } catch (err) {
      showError(err, 'email');
    }
  };

  const handleTestConnection = async (id: string) => {
    setTestingConfigId(id);
    try {
      const { data } = await emailApi.testStoreConfig(id);
      if (data.success) {
        showSuccess('save', { message: data.message || 'Testowy email został wysłany.' });
      } else {
        showError(data.error || 'Błąd wysyłki testowej', 'email');
      }
    } catch (err) {
      showError(err, 'email');
    } finally {
      setTestingConfigId(null);
    }
  };

  // Inline form mode
  if (showConfigForm || editingConfig) {
    return (
      <StoreEmailConfigFormInline
        storeId={store.id}
        storeName={store.name}
        storeUrl={store.url}
        config={editingConfig}
        onSave={() => {
          setShowConfigForm(false);
          setEditingConfig(null);
          onRefresh();
        }}
        onCancel={() => {
          setShowConfigForm(false);
          setEditingConfig(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-text-secondary text-sm">
          Konta SMTP do wysyłania email z tego sklepu (faktury, powiadomienia, wsparcie).
        </p>
        <button
          onClick={() => setShowConfigForm(true)}
          className="btn-primary text-sm flex items-center gap-1 flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Dodaj konto SMTP
        </button>
      </div>

      {emailConfigs.length === 0 ? (
        <div className="bg-surface-hover/30 rounded-xl">
          <EmptyState
            icon={Server}
            title="Brak skonfigurowanych kont SMTP"
            description="Dodaj konto SMTP aby móc wysyłać emaile z tego sklepu"
            size="sm"
          />
        </div>
      ) : (
        <div className="space-y-2">
          {emailConfigs.map(config => (
            <div
              key={config.id}
              className="flex items-center justify-between p-3 bg-surface-hover/30 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded ${config.isActive ? 'bg-success/20' : 'bg-surface-hover/50'}`}>
                  <Mail className={`w-4 h-4 ${config.isActive ? 'text-success' : 'text-text-muted'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary font-medium">{config.name}</span>
                    {config.isDefault && (
                      <span className="text-xxs px-1.5 py-0.5 bg-accent-primary/20 text-accent-primary rounded">Domyślny</span>
                    )}
                    {!config.isActive && (
                      <span className="text-xxs px-1.5 py-0.5 bg-surface-hover/50 text-text-secondary rounded">Nieaktywny</span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted">
                    {config.fromName} &lt;{config.fromEmail}&gt; • {config.host}:{config.port}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!config.isDefault && config.isActive && (
                  <button onClick={() => handleSetDefault(config.id)} className="p-1.5 text-text-secondary hover:text-accent-primary transition-colors" title="Ustaw jako domyślny">
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleTestConnection(config.id)}
                  disabled={testingConfigId === config.id}
                  className="p-1.5 text-text-secondary hover:text-success transition-colors"
                  title="Wyślij realny test SMTP na adres zalogowanego użytkownika"
                  aria-label="Wyślij realny test SMTP na adres zalogowanego użytkownika"
                >
                  {testingConfigId === config.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <FlaskConical className="w-4 h-4" />}
                </button>
                <button onClick={() => setEditingConfig(config)} className="p-1.5 text-text-secondary hover:text-text-primary transition-colors" title="Edytuj">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDeleteConfig(config.id)} className="p-1.5 text-text-secondary hover:text-danger transition-colors" title="Usuń">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// IMAP TAB — Inbox config list + inline add/edit form
// =============================================================================

function ImapTabContent({
  store,
  inboxConfigs,
  emailConfigs,
  onRefresh,
}: {
  store: Store;
  inboxConfigs: any[];
  emailConfigs: StoreEmailConfig[];
  onRefresh: () => void;
}) {
  const confirmDialog = useConfirmDialog();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const [form, setForm] = useState({
    name: '', host: '', port: 993, secure: true, user: '', password: '', fromEmail: '', smtpConfigId: '', isActive: true,
  });

  const resetForm = () => {
    setForm({ name: '', host: '', port: 993, secure: true, user: '', password: '', fromEmail: '', smtpConfigId: '', isActive: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (cfg: any) => {
    setEditingId(cfg.id);
    setForm({
      name: cfg.name,
      host: cfg.host,
      port: cfg.port,
      secure: cfg.secure,
      user: cfg.user,
      password: '',
      fromEmail: cfg.fromEmail,
      smtpConfigId: cfg.smtpConfigId || '',
      isActive: cfg.isActive,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        const updateData: Record<string, unknown> = {
          ...form,
          storeId: store.id,
          smtpConfigId: form.smtpConfigId || null,
        };
        if (!form.password) delete updateData.password;
        await inboxEmailApi.updateConfig(editingId, updateData);
      } else {
        await inboxEmailApi.createConfig({
          ...form,
          storeId: store.id,
          smtpConfigId: form.smtpConfigId || null,
        });
      }
      resetForm();
      onRefresh();
    } catch (error) {
      showError(error, 'email');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń skrzynkę',
      message: 'Usunąć tę skrzynkę?',
      confirmLabel: 'Usuń',
    }))) return;
    await inboxEmailApi.deleteConfig(id);
    onRefresh();
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    try {
      const res = await inboxEmailApi.testConfig(id);
      if (res.data.success) {
        showSuccess('save', { message: res.data.message || 'Połączenie IMAP OK' });
      } else {
        showError(res.data.message || 'Błąd połączenia IMAP', 'email');
      }
    } catch {
      showError('Błąd połączenia IMAP', 'email');
    } finally {
      setTesting(null);
    }
  };

  const handleSync = async (id: string) => {
    setSyncing(id);
    try {
      const res = await inboxEmailApi.syncConfig(id);
      showSuccess('save', { message: res.data.message || 'Synchronizacja zakończona' });
      onRefresh();
    } catch {
      showError('Błąd synchronizacji', 'email');
    } finally {
      setSyncing(null);
    }
  };

  // Inline form mode
  if (showForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-text-primary font-medium">
            {editingId ? 'Edytuj skrzynkę odbiorczą' : 'Dodaj skrzynkę odbiorczą'}
          </h4>
          <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Nazwa konta *</label>
            <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="input w-full" placeholder="np. Główna skrzynka" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-text-secondary mb-1 block">Host IMAP *</label>
              <input type="text" value={form.host} onChange={(e) => setForm(f => ({ ...f, host: e.target.value }))} className="input w-full" placeholder="imap.gmail.com" />
            </div>
            <div>
              <label className="text-xs text-text-secondary mb-1 block">Port</label>
              <input type="number" value={form.port} onChange={(e) => setForm(f => ({ ...f, port: parseInt(e.target.value) || 993 }))} className="input w-full" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-secondary mb-1 block">Login (użytkownik) *</label>
              <input type="text" value={form.user} onChange={(e) => setForm(f => ({ ...f, user: e.target.value }))} className="input w-full" placeholder="user@example.com" />
            </div>
            <div>
              <label className="text-xs text-text-secondary mb-1 block">Hasło {editingId ? '(zostaw puste = bez zmian)' : '*'}</label>
              <input type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} className="input w-full" />
            </div>
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Adres email (wyświetlany) *</label>
            <input type="email" value={form.fromEmail} onChange={(e) => setForm(f => ({ ...f, fromEmail: e.target.value }))} className="input w-full" placeholder="kontakt@sklep.pl" />
          </div>
          <div>
            <label className="text-xs text-text-secondary mb-1 block">Połączenie SMTP do odpowiedzi</label>
            <Select value={form.smtpConfigId} onChange={(e) => setForm(f => ({ ...f, smtpConfigId: e.target.value }))}>
              <option value="">Domyślne aktywne konto SMTP</option>
              {emailConfigs.filter(config => config.isActive).map(config => (
                <option key={config.id} value={config.id}>{config.name} - {config.fromEmail}</option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input type="checkbox" checked={form.secure} onChange={(e) => setForm(f => ({ ...f, secure: e.target.checked }))} className="rounded border-border" />
              SSL/TLS
            </label>
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded border-border" />
              Aktywna
            </label>
          </div>
          <div className="bg-accent-primary/5 border border-accent-primary/10 rounded-lg p-3 text-xs text-text-secondary">
            <p className="font-medium text-accent-primary mb-1">Jak uzyskać dane IMAP?</p>
            <p>Gmail: imap.gmail.com:993 (SSL). Wymagane hasło aplikacji.</p>
            <p>Outlook: outlook.office365.com:993 (SSL).</p>
            <p>Własny serwer: Sprawdź u dostawcy hostingu.</p>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving || !form.name || !form.host || !form.user || !form.fromEmail || (!editingId && !form.password)}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {editingId ? 'Zapisz zmiany' : 'Dodaj skrzynkę'}
          </button>
          <button onClick={resetForm} className="btn-secondary flex-1">
            Anuluj
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-text-secondary text-sm">
          Skrzynki odbiorcze (IMAP) — wiadomości będą widoczne w module Wiadomości.
        </p>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="btn-primary text-sm flex items-center gap-1 flex-shrink-0"
        >
          <Plus className="w-4 h-4" /> Dodaj skrzynkę
        </button>
      </div>

      {inboxConfigs.length === 0 ? (
        <div className="bg-surface-hover/30 rounded-xl">
          <EmptyState
            icon={Inbox}
            title="Brak skrzynek odbiorczych"
            description="Dodaj konfigurację IMAP, aby odbierać wiadomości email."
            size="sm"
          />
        </div>
      ) : (
        <div className="space-y-2">
          {inboxConfigs.map((cfg: any) => (
            <div key={cfg.id} className="flex items-center justify-between p-3 bg-surface-hover/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded ${cfg.isActive ? 'bg-success/20' : 'bg-surface-hover/50'}`}>
                  <Inbox className={`w-4 h-4 ${cfg.isActive ? 'text-success' : 'text-text-muted'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary font-medium">{cfg.name}</span>
                    {cfg.isActive ? (
                      <span className="text-xxs px-1.5 py-0.5 bg-success/10 text-success rounded-full">Aktywna</span>
                    ) : (
                      <span className="text-xxs px-1.5 py-0.5 bg-surface-hover text-text-muted rounded-full">Nieaktywna</span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted">{cfg.fromEmail} • {cfg.host}:{cfg.port} {cfg.secure ? '(SSL)' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleSync(cfg.id)} className="p-1.5 text-text-secondary hover:text-accent-primary transition-colors" title="Synchronizuj teraz">
                  {syncing === cfg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
                <button onClick={() => handleTest(cfg.id)} className="p-1.5 text-text-secondary hover:text-success transition-colors" title="Test połączenia">
                  {testing === cfg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                </button>
                <button onClick={() => handleEdit(cfg)} className="p-1.5 text-text-secondary hover:text-text-primary transition-colors" title="Edytuj">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(cfg.id)} className="p-1.5 text-text-secondary hover:text-danger transition-colors" title="Usuń">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TEMPLATES TAB — Store templates list + inline editor
// =============================================================================

// (TemplatesTabContent and GeneralTemplatesSection removed — moved to SettingsPage > email-templates)

// =============================================================================
// INLINE STORE EMAIL CONFIG FORM (SMTP) — unchanged
// =============================================================================

function StoreEmailConfigFormInline({
  storeId,
  storeName,
  storeUrl,
  config,
  onSave,
  onCancel,
}: {
  storeId: string;
  storeName: string;
  storeUrl: string;
  config: StoreEmailConfig | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    storeId,
    name: config?.name || '',
    host: config?.host || '',
    port: config?.port || 587,
    secure: config?.secure ?? true,
    user: config?.user || '',
    password: '',
    fromEmail: config?.fromEmail || '',
    fromName: config?.fromName || '',
    phone: config?.phone || '',
    isDefault: config?.isDefault || false,
    isActive: config?.isActive ?? true,
  });

  const handleSave = async () => {
    if (!formData.name || !formData.host || !formData.user || !formData.fromEmail || !formData.fromName) {
      setError('Wypełnij wszystkie wymagane pola');
      return;
    }

    if (!config && !formData.password) {
      setError('Hasło jest wymagane');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (config) {
        const updateData: Record<string, unknown> = { ...formData };
        if (!formData.password) {
          delete updateData.password;
        }
        await emailApi.updateStoreConfig(config.id, updateData as Parameters<typeof emailApi.updateStoreConfig>[1]);
      } else {
        await emailApi.createStoreConfig(formData as Parameters<typeof emailApi.createStoreConfig>[0]);
      }
      onSave();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      if (!config) {
        setError('Najpierw zapisz konfigurację aby móc ją przetestować');
        setTesting(false);
        return;
      }

      const { data } = await emailApi.testStoreConfig(config.id);
      setTestResult({
        success: data.success,
        message: data.success ? (data.message || 'Testowy email został wysłany.') : (data.error || 'Test nieudany'),
      });
    } catch (err) {
      console.error('Test error:', err);
      setTestResult({
        success: false,
        message: 'Błąd podczas testowania',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">
            {config ? 'Edytuj konto email' : 'Nowe konto email'}
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Sklep: {storeName} ({storeUrl})
          </p>
        </div>
        <button onClick={onCancel} className="text-text-secondary hover:text-text-primary">
          <XCircle className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="flex items-center p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">
          <XCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      {testResult && (
        <div className={`flex items-center p-3 border rounded-lg text-sm ${
          testResult.success
            ? 'bg-success/10 border-success/30 text-success'
            : 'bg-danger/10 border-danger/30 text-danger'
        }`}>
          {testResult.success ? <CheckCircle className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
          {testResult.message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-text-secondary mb-2">Nazwa konta *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="np. Główne konto, Faktury, Wsparcie"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Host SMTP *</label>
          <input
            type="text"
            value={formData.host}
            onChange={(e) => setFormData({ ...formData, host: e.target.value })}
            placeholder="smtp.gmail.com"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Port</label>
          <input
            type="number"
            value={formData.port}
            onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 587 })}
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Użytkownik SMTP *</label>
          <input
            type="text"
            value={formData.user}
            onChange={(e) => setFormData({ ...formData, user: e.target.value })}
            placeholder="user@example.com"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Hasło {config ? '(zostaw puste aby nie zmieniać)' : '*'}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              className="input pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Email nadawcy *</label>
          <input
            type="email"
            value={formData.fromEmail}
            onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
            placeholder="sklep@example.com"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Nazwa nadawcy *</label>
          <input
            type="text"
            value={formData.fromName}
            onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
            placeholder="Mój Sklep"
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">Telefon sklepu (opcjonalnie)</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+48 123 456 789"
            className="input"
          />
          <p className="text-xs text-text-muted mt-1">Używany w szablonach email jako {"{{sklep_telefon}}"}</p>
        </div>

        <div className="col-span-2 flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.secure}
              onChange={(e) => setFormData({ ...formData, secure: e.target.checked })}
              className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-text-primary text-sm flex items-center gap-1">
              <Lock className="w-3 h-3" />
              SSL/TLS
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-text-primary text-sm">Domyślne konto</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-text-primary text-sm">Aktywne</span>
          </label>
        </div>

      </div>

      <div className="flex items-center gap-3 pt-4 border-t border-border">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Zapisz
        </button>
        <button onClick={onCancel} className="btn-secondary">
          Anuluj
        </button>

        <div className="flex-1" />

        {config && (
          <button
            onClick={handleTest}
            disabled={testing}
            className="btn-secondary flex items-center gap-2"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            Wyślij realny test SMTP
          </button>
        )}
      </div>
    </div>
  );
}

export default EmailSettingsPage;
